/**
 * APIを呼ぶ共通の窓口。画面からは必ずここを通す。
 *
 * ここに集めている決まりごと:
 *  - 呼び先は `/api` から始まる相対パス（画面とAPIは同じ入口から配られる）
 *  - 通行証（トークン）は毎回ヘッダに付ける。画面の各所では持ち回らない
 *  - 通行証の期限が切れた（401）ら、**ここで1回だけ取り直して**、元の呼び出しをやり直す
 *  - 取り直せなかったときだけ、1か所でログイン画面へ戻す
 *  - 失敗したときの言葉は、運用される方が読んで分かる日本語にする
 */

/** 既定の呼び先。Caddy が `/api` を外してAPIサーバへ渡す（infra/caddy/Caddyfile）。 */
export const DEFAULT_API_BASE_URL = '/api';

/** APIの呼び出しが失敗したときに投げるもの。message はそのまま画面に出せる日本語。 */
export class ApiError extends Error {
  /** HTTP の状態番号（つながらなかったときは 0） */
  readonly status: number;
  /** サーバが返した識別子（記録・切り分け用。画面には出さない） */
  readonly code: string;
  /**
   * 時間をおいて同じ操作をやり直せば通る見込みがあるか（サーバが `retryable` で返す）。
   * 返さない古いサーバでは undefined（そのときは状態番号で見分ける）
   */
  readonly retryable: boolean | undefined;

  constructor(status: number, code: string, message: string, retryable?: boolean) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryable = retryable;
  }
}

/** 状態番号ごとの言い換え（サーバが日本語の説明を返さなかったときの備え）。 */
export function messageForStatus(status: number): string {
  if (status === 0) return 'サーバにつながりませんでした。通信の状態をご確認ください。';
  if (status === 400) return '入力の内容に誤りがあります。';
  if (status === 401) return 'ログインの有効期限が切れました。もう一度ログインしてください。';
  if (status === 403) return 'この操作を行う権限がありません。';
  if (status === 404) return '対象が見つかりませんでした。';
  if (status === 429) return '短い時間に何度も実行されました。少し時間をおいてお試しください。';
  // 次の一手（「少し時間をおいて…」）は画面の側で添える（#334 L-1。同じお願いを2回出さない）
  if (status >= 500) return 'サーバで問題が起きました。';
  return '実行できませんでした。時間をおいてもう一度お試しください。';
}

export interface RequestOptions {
  /** 問い合わせの絞り込み（値が undefined の項目は送らない） */
  query?: Record<string, string | number | undefined>;
  /** 送る中身（POST のとき） */
  body?: unknown;
  /** 通行証を付けずに呼ぶ（ログインの呼び出しだけ true） */
  anonymous?: boolean;
  /**
   * 401 のときにログイン画面へ戻す処理を呼ばない。
   * ログインの画面自身（入力し直してもらう場面）だけ true にする。
   */
  keepSessionOnUnauthorized?: boolean;
}

/** 取り直しの結果（issue #363）。 */
type RefreshOutcome = 'ok' | 'rejected' | 'unreachable';

/** ログイン状態を作り直すための組（サーバの `/auth/refresh` が返す形）。 */
export interface RefreshedTokens {
  access_token: string;
  refresh_token: string;
}

export interface ApiClientOptions {
  baseUrl?: string;
  /** いまの通行証を取り出す */
  getToken: () => string | null;
  /**
   * 取り直し用の控えを取り出す。
   * 通行証の期限（既定15分）が切れても、これで取り直せる限りログインし直していただかない。
   */
  getRefreshToken?: () => string | null;
  /** 取り直せたときに呼ばれる（新しい組を置き場へ入れる） */
  onRefreshed?: (tokens: RefreshedTokens) => void;
  /** 取り直せなかったときに1回だけ呼ばれる（ログイン画面へ戻す） */
  onUnauthorized: () => void;
  /** テストから差し替えるため（既定はブラウザの fetch） */
  fetchImpl?: typeof fetch;
}

export interface ApiClient {
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  post<T>(path: string, options?: RequestOptions): Promise<T>;
  /** 一部の項目だけを書き換える（部屋の名前・設定・保存日数、AI先輩の回答の訂正など） */
  patch<T>(path: string, options?: RequestOptions): Promise<T>;
  /** 消す（部屋の削除・参加者を外す）。応答が空でも落ちない */
  del<T>(path: string, options?: RequestOptions): Promise<T>;
  /**
   * ファイルを置き場へ直接置く（スタンプの素材など）。
   *
   * 呼び先はサーバが発行した**書き込み用の短い期限のリンク**そのもの（`/api` の外）で、
   * 通行証は付けません（リンク自体が許可を持つため。付けると置き場に余計なものが渡る）。
   * サーバが指定したヘッダ（形式など）は、そのまま付けます。
   */
  putFile(url: string, file: Blob, headers?: Record<string, string>): Promise<void>;
}

/** 同じブラウザのタブどうしで、控えの取り直しを1本ずつにするための名前（issue #404）。 */
const REFRESH_LOCK_NAME = 'field-chat.admin.refresh';

/**
 * 同じブラウザのタブどうしで順番に行う（ブラウザの「名前つきの鍵」・Web Locks）。
 * 使えないブラウザ・試験の環境では、そのまま行う。
 */
async function withTabLock<T>(work: () => Promise<T>): Promise<T> {
  const locks =
    typeof navigator !== 'undefined'
      ? (navigator as Navigator & { locks?: LockManager }).locks
      : undefined;
  if (!locks || typeof locks.request !== 'function') return work();
  return (await locks.request(REFRESH_LOCK_NAME, () => work())) as T;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl ?? DEFAULT_API_BASE_URL;
  const doFetch = options.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  /**
   * 取り直しの進行中の約束。
   * 同時に走った呼び出しが揃って 401 になったとき、取り直しを何本も走らせない
   * （控えは1回使うと入れ替わるので、2本目以降は必ず失敗してしまう）。
   */
  let refreshing: Promise<RefreshOutcome> | null = null;

  /**
   * 控えで通行証を取り直す。
   *
   * - `ok` … 取り直せた
   * - `rejected` … サーバが控えを受け付けなかった（期限切れ・使えなくなった）
   * - `unreachable` … つながらなかった・サーバの不調。**ログインは消さない**（issue #363。
   *   通信の瞬断やサーバの入れ替え中に、SMS からやり直しにならないように）
   */
  async function refreshTokens(): Promise<RefreshOutcome> {
    const refreshToken = options.getRefreshToken?.() ?? null;
    if (!refreshToken || !options.onRefreshed) return 'rejected';
    let response: Response;
    try {
      response = await doFetch(`${baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { accept: 'application/json', 'content-type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      return 'unreachable';
    }
    if (response.status >= 500 || response.status === 429 || response.status === 408) {
      return 'unreachable';
    }
    if (!response.ok) {
      // 同じブラウザのほかのタブが先に取り直していた（控えが入れ替わっていた）ときは、
      // そちらの新しい組を使えばよい。締め出さない（issue #363）
      const latest = options.getRefreshToken?.() ?? null;
      return latest !== null && latest !== refreshToken ? 'ok' : 'rejected';
    }
    const payload = (await readJson(response)) as Partial<RefreshedTokens> | undefined;
    if (typeof payload?.access_token !== 'string' || typeof payload.refresh_token !== 'string') {
      return 'unreachable';
    }
    options.onRefreshed({
      access_token: payload.access_token,
      refresh_token: payload.refresh_token,
    });
    return 'ok';
  }

  /**
   * 取り直しは同時に1本だけ。ほかの呼び出しはその結果を待つ。
   * **同じブラウザのほかのタブとも1本ずつ**にする（issue #404）。使い終わった控えが出されると、
   * サーバはそのログインを丸ごと切るので、2つのタブが同じ控えで同時に取り直すと、両方のタブが
   * ログインの画面へ戻ってしまう。順番待ちのあとは、ほかのタブが入れ替えた新しい控えを読んで使う
   */
  function refreshOnce(): Promise<RefreshOutcome> {
    refreshing ??= withTabLock(refreshTokens).finally(() => {
      refreshing = null;
    });
    return refreshing;
  }

  async function request<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    path: string,
    requestOptions: RequestOptions = {},
    /** 取り直したあとのやり直しかどうか（やり直しは1回まで） */
    isRetry = false,
  ): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (!requestOptions.anonymous) {
      const token = options.getToken();
      if (token) headers.authorization = `Bearer ${token}`;
    }
    if (requestOptions.body !== undefined) headers['content-type'] = 'application/json';

    let response: Response;
    try {
      response = await doFetch(baseUrl + path + buildQuery(requestOptions.query), {
        method,
        headers,
        ...(requestOptions.body !== undefined ? { body: JSON.stringify(requestOptions.body) } : {}),
      });
    } catch {
      // つながらなかった（回線・サーバ停止）。握りつぶさず、読める言葉にして投げ直す
      throw new ApiError(0, 'network_error', messageForStatus(0));
    }

    if (response.status === 401 && !requestOptions.keepSessionOnUnauthorized) {
      // まず控えで取り直してみる。取り直せたら、同じ呼び出しを1回だけやり直す
      // （通行証の期限は既定15分。ここが無いと、15分ごとにログインし直すことになる）
      if (!isRetry && !requestOptions.anonymous) {
        const outcome = await refreshOnce();
        if (outcome === 'ok') return request<T>(method, path, requestOptions, true);
        if (outcome === 'unreachable') {
          // 取り直しの窓口につながらなかっただけ。ログインは残し、つながらなかったとお伝えする
          throw new ApiError(0, 'network_error', messageForStatus(0));
        }
      }
      options.onUnauthorized();
    }

    if (!response.ok) {
      const payload = (await readJson(response)) as
        { error?: unknown; message?: unknown; retryable?: unknown } | undefined;
      const code = typeof payload?.error === 'string' ? payload.error : 'error';
      // サーバの説明は日本語で返る（形式の確かめで断るときも日本語の決まった文面・issue #248）。
      // 何が悪かったか（「合い言葉は英小文字で…」など）が分かるよう、400 でもそのまま出す。
      // 念のため、日本語を含まない説明（古いサーバ・土台の英語の文面）だけは言い換える
      const message =
        typeof payload?.message === 'string' && hasJapanese(payload.message)
          ? payload.message
          : messageForStatus(response.status);
      throw new ApiError(
        response.status,
        code,
        message,
        typeof payload?.retryable === 'boolean' ? payload.retryable : undefined,
      );
    }

    if (response.status === 204) return undefined as T;
    return (await readJson(response)) as T;
  }

  return {
    get: (path, requestOptions) => request('GET', path, requestOptions),
    post: (path, requestOptions) => request('POST', path, requestOptions),
    patch: (path, requestOptions) => request('PATCH', path, requestOptions),
    del: (path, requestOptions) => request('DELETE', path, requestOptions),
    putFile: async (url, file, headers = {}) => {
      let response: Response;
      try {
        response = await doFetch(url, { method: 'PUT', headers, body: file });
      } catch {
        throw new ApiError(0, 'network_error', messageForStatus(0));
      }
      if (!response.ok) {
        // 置き場の断り文は英語（XML）なので出さない。状態番号で言い換える
        throw new ApiError(
          response.status,
          'upload_failed',
          '画像を置けませんでした。時間をおいて、もう一度お試しください。',
        );
      }
    },
  };
}

/** ひらがな・カタカナ・漢字を1文字でも含むか（画面にそのまま出してよい説明かの目安）。 */
function hasJapanese(text: string): boolean {
  return /[\u3040-\u30ff\u3400-\u9fff]/.test(text);
}

function buildQuery(query: RequestOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    params.set(key, String(value));
  }
  const text = params.toString();
  return text.length > 0 ? `?${text}` : '';
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    // 中身が JSON でないときは、状態番号だけで判断する（ここで落とさない）
    return undefined;
  }
}
